import Layout from "@/components/layout/Layout"
import MainHeading from "@/components/common/MainHeading"
import Card from "@/components/common/Card"
import FormField from "@/components/common/FormField"
import { useForm } from "react-hook-form"
import { errorTypes } from "@/utility/validation"
import type { branch, branchResponse } from "@/interface/branch.interface"
import { useEffect, useState } from "react"
import { ThrowError } from "@/api/callApi"
import { showToastNotification } from "@/utility/helper"
import { RoutesLink } from "@/utility/RouteLinks"
import { BranchService } from "@/services/Branch/BranchSevice"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import FullPageLoader from "@/components/common/FullPageLoader"
import states from "@/utility/states.json"

const fetchPincodeDetails = async (pin: string) => {
    if (!pin || pin.length !== 6 || !/^\d{6}$/.test(pin)) return null;
    try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await response.json();
        if (Array.isArray(data) && data[0]?.Status === 'Success' && data[0]?.PostOffice?.length > 0) {
            const postOffice = data[0].PostOffice[0];
            return {
                district: postOffice.District,
                state: postOffice.State,
            };
        }
    } catch (e) {
        console.error("Error fetching pincode details:", e);
    }
    return null;
};

const AddBranch: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<branch>({
        mode: "onChange",
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isFatchLoading, setIsFatchLoading] = useState(false);

    const pinCode = watch("pinCode");

    useEffect(() => {
        if (pinCode && pinCode.length === 6 && /^\d{6}$/.test(pinCode)) {
            const getDetails = async () => {
                const details = await fetchPincodeDetails(pinCode);
                if (details) {
                    if (details.state) {
                        setValue("state", details.state, { shouldValidate: true, shouldDirty: true });
                    }
                    if (details.district) {
                        setValue("city", details.district, { shouldValidate: true, shouldDirty: true });
                    }
                }
            };
            getDetails();
        }
    }, [pinCode, setValue]);
    const onSubmit = async (data: branch) => {
        try {
            setIsLoading(true)
            const payload = {
                name: data.name,
                gst: data.gst,
                phone: data.phone,
                email: data.email,
                secondaryPhone: data.secondaryPhone,
                secondaryEmail: data.secondaryEmail,
                address: data.address,
                city: data.city,
                state: data.state,
                pinCode: data.pinCode,
                pan: data.pan,
                tANDc: data.tANDc,
                shortName: data.shortName,
                status: data.status,
            }
            let response: branchResponse;
            if (id) {
                response = await BranchService?.updateBranch(id, payload);
            } else {
                response = await BranchService?.createBranch(payload);
            }
            if (response?.status) {
                showToastNotification("success", response?.message)
                navigate(RoutesLink.superAdminBranch)
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error)
        }
        finally {
            setIsLoading(false)
        }
    }
    const handleEdit = async (id: string) => {
        try {
            setIsFatchLoading(true)
            const response = await BranchService?.getBranchById(id);
            if (response?.status) {
                const editData: branch = {
                    name: response?.data?.name,
                    gst: response?.data?.gst,
                    phone: response?.data?.phone,
                    email: response?.data?.email,
                    secondaryPhone: response?.data?.secondaryPhone,
                    secondaryEmail: response?.data?.secondaryEmail,
                    address: response?.data?.address,
                    city: response?.data?.city,
                    state: response?.data?.state,
                    pinCode: response?.data?.pinCode,
                    pan: response?.data?.pan,
                    tANDc: response?.data?.tANDc,
                    shortName: response?.data?.shortName,
                    status: response?.data?.status,
                }
                reset(editData)
                // showToastNotification("success", response?.message)
                // navigate(RoutesLink.superAdminBranch)
            } else {
                showToastNotification("danger", response?.message)
            }
            // console.log(data);
        } catch (error) {
            ThrowError(error)
        }
        finally {
            setIsFatchLoading(false)
        }
    }
    useEffect(() => {
        if (id) {
            handleEdit(id)
        }
    }, []);

    if (isFatchLoading) {
        return (
            <Layout>
                {/* <div className="flex items-center justify-center min-h-[400px]"> */}
                <FullPageLoader />
                {/* <Spinner className="h-8 w-8 text-primary animate-spin" /> */}
                {/* </div> */}
            </Layout >
        );
    }
    return (
        <>
            <Layout>
                <div className="space-y-6">
                    <MainHeading title={id ? "Edit Branch" : "Add Branch"} isBackButtonVisible />
                    <Card>
                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    {...register("name", { ...errorTypes.required('Name'), ...errorTypes.name, })}
                                    type="text"
                                    name="name"
                                    label="Name"
                                    maxLength={30}
                                    placeholder="Enter Branch Name"
                                    error={errors.name?.message}
                                    required
                                />

                                <FormField
                                    {...register("gst", { ...errorTypes.required('GST'), ...errorTypes.gstNumber })}
                                    type="text"
                                    name="gst"
                                    label="GST"
                                    maxLength={15}
                                    placeholder="Enter GST"
                                    error={errors.gst?.message}
                                    required
                                />
                                <FormField
                                    {...register("phone", { ...errorTypes.required('Phone'), ...errorTypes.phone })}
                                    type="text"
                                    name="phone"
                                    label="Phone"
                                    placeholder="Enter Phone"
                                    maxLength={10}
                                    minLength={10}
                                    pattern="[0-9]{10}"
                                    error={errors.phone?.message}
                                    required

                                />
                                <FormField
                                    {...register("email", { ...errorTypes.required('Email'), ...errorTypes.email, })}
                                    type="email"
                                    name="email"
                                    label="Email"
                                    placeholder="Enter Email"
                                    error={errors.email?.message}
                                    required
                                />
                                <FormField
                                    {...register("secondaryPhone", { ...errorTypes.phone })}
                                    type="text"
                                    name="secondaryPhone"
                                    label="Secondary Phone"
                                    placeholder="Enter Secondary Phone"
                                    maxLength={10}
                                    minLength={10}
                                    pattern="[0-9]{10}"
                                    error={errors.secondaryPhone?.message}
                                    required

                                />
                                <FormField
                                    {...register("secondaryEmail", { ...errorTypes.email })}
                                    type="email"
                                    name="secondaryEmail"
                                    label="Secondary Email"
                                    placeholder="Enter Secondary Email"
                                    error={errors.secondaryEmail?.message}
                                    required
                                />
                                <FormField
                                    {...register("address", { ...errorTypes.required('Address'), ...errorTypes.address })}
                                    type="text"
                                    name="address"
                                    label="Address"
                                    maxLength={100}
                                    placeholder="Enter Address"
                                    error={errors.address?.message}
                                    required
                                />
                                <FormField
                                    {...register("pinCode", {
                                        ...errorTypes.required('Pin Code'),
                                        ...errorTypes.pinCode,
                                    })}
                                    type="text"
                                    name="pinCode"
                                    label="Pin Code (Auto Fetch State & City)"
                                    placeholder="Enter Pin Code"
                                    maxLength={6}
                                    minLength={6}
                                    error={errors.pinCode?.message}
                                    required
                                />
                                <FormField
                                    {...register("state", { ...errorTypes.required('State') })}
                                    type="select"
                                    name="state"
                                    label="State"
                                    placeholder="Select State"
                                    options={states}
                                    value={watch("state") || ""}
                                    error={errors.state?.message}
                                    required
                                />
                                <FormField
                                    {...register("city", { ...errorTypes.required('City'), ...errorTypes.city })}
                                    type="text"
                                    name="city"
                                    label="City"
                                    maxLength={30}
                                    placeholder="Enter City"
                                    error={errors.city?.message}
                                    required
                                />
                                <FormField
                                    {...register("pan", { ...errorTypes.required('PAN'), ...errorTypes.pan })}
                                    type="text"
                                    name="pan"
                                    label="PAN"
                                    maxLength={10}
                                    minLength={10}
                                    placeholder="Enter PAN"
                                    error={errors.pan?.message}
                                    required

                                />

                                <FormField
                                    {...register("shortName", { ...errorTypes.required('Short Name'), ...errorTypes.onlyAlphabets })}
                                    type="text"
                                    name="shortName"
                                    label="Short Name"
                                    maxLength={50}
                                    placeholder="Enter Short Name"
                                    error={errors.shortName?.message}
                                    required
                                />
                                <FormField
                                    {...register("status", { ...errorTypes.required('Status') })}
                                    type="select"
                                    value={String(watch("status") ?? "")}
                                    options={[
                                        { value: "select", label: "Select Status" },
                                        { value: "true", label: "True" },
                                        { value: "false", label: "False" },
                                    ]}
                                    name="status"
                                    label="Status"
                                    placeholder="Enter Status"
                                    error={errors.status?.message}
                                    required
                                />
                                <FormField
                                    {...register("tANDc", { ...errorTypes.required('T&C'), ...errorTypes.description })}
                                    type="textarea"
                                    name="tANDc"
                                    rows={5}
                                    label="T&C"
                                    maxLength={500}
                                    placeholder="Enter T&C"
                                    error={errors.tANDc?.message}
                                    required
                                />
                            </div>
                            <div className="flex justify-end">
                                <Button disabled={isLoading} type="submit">{isLoading ? "Submitting..." : "Submit"}</Button>

                            </div>
                        </form>
                    </Card>
                </div>
            </Layout>
        </>
    )
}

export default AddBranch